import { Injectable } from '@angular/core';

@Injectable()
export class ExcelReportService {
    private readonly BASE_URL = 'https://operacion.trosten.com/excel-service/';
    private readonly AUTH_TOKEN_RAW = 'RfILK889PNZmkBi5trx3ZFPy95Ku2qQKSnRjJC5m';
    private readonly REPORT_PASSWORD = 'Trosten023226';

    constructor() {}

    /**
     * Validates if the provided password matches the expected report password
     * @param password Password to validate
     * @returns true if password is correct, false otherwise
     */
    validateReportPassword(password: string): boolean {
        return password === this.REPORT_PASSWORD;
    }

    /**
     * Generates the base64 encoded authentication token
     * @returns Base64 encoded authentication token
     */
    private generateAuthToken(): string {
        return btoa(this.AUTH_TOKEN_RAW);
    }

    /**
     * Formats a date string to include time component for start of day
     * @param date Date string in format YYYY-MM-DD
     * @returns Date string with time " 00:00:00"
     */
    formatStartDateTime(date: string): string {
        return date + ' 00:00:00';
    }

    /**
     * Formats a date string to include time component for end of day
     * @param date Date string in format YYYY-MM-DD
     * @returns Date string with time " 23:59:59"
     */
    formatEndDateTime(date: string): string {
        return date + ' 23:59:59';
    }

    /**
     * Validates that initial date is not greater than final date
     * @param fechaInicial Initial date string
     * @param fechaFinal Final date string
     * @returns true if dates are valid, false otherwise
     */
    validateDateRange(fechaInicial: string, fechaFinal: string): boolean {
        return fechaInicial <= fechaFinal;
    }

    /**
     * Builds URL for sales report (reporteVentas.php)
     * @param fechaInicial Start date
     * @param fechaFinal End date
     * @returns Complete URL for the sales report
     */
    buildSalesReportUrl(fechaInicial: string, fechaFinal: string): string {
        const authToken = this.generateAuthToken();
        return this.BASE_URL + 'reporteVentas.php' +
            '?fInicial=' + fechaInicial +
            '&fechaFinal=' + fechaFinal +
            '&fhrtbxdbtrbrt=' + authToken;
    }

    /**
     * Builds URL for quotations client report (reporteCotizacionesCliente.php)
     * @param fechaInicial Start date
     * @param fechaFinal End date
     * @param tipo Report type (PORCREACION or PORINICIO)
     * @returns Complete URL for the quotations client report
     */
    buildQuotationsClientReportUrl(fechaInicial: string, fechaFinal: string, tipo: string): string {
        const authToken = this.generateAuthToken();
        return this.BASE_URL + 'reporteCotizacionesCliente.php' +
            '?fInicial=' + fechaInicial +
            '&fechaFinal=' + fechaFinal +
            '&tipo=' + tipo +
            '&fhrtbxdbtrbrt=' + authToken;
    }

    /**
     * Builds URL for clients without type report (reporteClientesSinTipoCliente.php)
     * @returns Complete URL for the clients without type report
     */
    buildClientsWithoutTypeReportUrl(): string {
        const authToken = this.generateAuthToken();
        return this.BASE_URL + 'reporteClientesSinTipoCliente.php' +
            '?fhrtbxdbtrbrt=' + authToken;
    }

    /**
     * Opens a report URL in a new browser tab
     * @param url Report URL to open
     */
    openReport(url: string): void {
        window.open(url, '_blank');
    }

    /**
     * Downloads sales report with date range validation
     * @param fechaInicial Start date (YYYY-MM-DD)
     * @param fechaFinal End date (YYYY-MM-DD)
     * @returns true if report was opened, false if validation failed
     */
    downloadSalesReport(fechaInicial: string, fechaFinal: string): boolean {
        const startDateTime = this.formatStartDateTime(fechaInicial);
        const endDateTime = this.formatEndDateTime(fechaFinal);

        if (!this.validateDateRange(startDateTime, endDateTime)) {
            alert('La fecha inicial no puede ser mayor a la final');
            return false;
        }

        const url = this.buildSalesReportUrl(startDateTime, endDateTime);
        this.openReport(url);
        return true;
    }

    /**
     * Downloads quotations client report with date range validation and password
     * @param fechaInicial Start date (YYYY-MM-DD)
     * @param fechaFinal End date (YYYY-MM-DD)
     * @param tipo Report type (PORCREACION or PORINICIO)
     * @param password User-provided password
     * @returns true if report was opened, false if validation failed
     */
    downloadQuotationsClientReport(fechaInicial: string, fechaFinal: string, tipo: string, password: string): boolean {
        if (!this.validateReportPassword(password)) {
            alert('Contraseña incorrecta, intentalo de nuevo.');
            return false;
        }

        const startDateTime = this.formatStartDateTime(fechaInicial);
        const endDateTime = this.formatEndDateTime(fechaFinal);

        if (!this.validateDateRange(startDateTime, endDateTime)) {
            alert('La fecha inicial no puede ser mayor a la final');
            return false;
        }

        const url = this.buildQuotationsClientReportUrl(startDateTime, endDateTime, tipo);
        this.openReport(url);
        return true;
    }

    /**
     * Downloads clients without type report (no parameters needed)
     */
    downloadClientsWithoutTypeReport(): void {
        const url = this.buildClientsWithoutTypeReportUrl();
        this.openReport(url);
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/excel-report.service.ts